import React from 'react'
import PropTypes from 'prop-types'
import { GatsbyImage, getImage } from 'gatsby-plugin-image'
import { renderTextWithLink } from '../Utilities/TextWithLink'
import BlockCard from '../Cards/BlockCard'
import ContentfulButton from '../Utilities/Buttons/ContentfulButton'
import ContentfulForm from '../Utilities/ContentfulForm'
import { CurvedArrow } from '../icons'
import { TypeWriter } from '../Utilities'
import PeopleChoose from '../PeopleChoose'
import RatesTermsRibbon from '../RatesTermsRibbon'
import FeatureV2 from '../FeatureV2'
import PromoCard from '../Cards/PromoCard'

function CustomHero({ data, slug, carouselIndex }) {
  const CONTENT_TYPE_MP4 = 'video/mp4'
  const CONTENT_TYPE_WEBM = 'video/webm'

  const {
    headline,
    headlineLogos,
    backgroundMedia,
    image,
    imageCard,
    theme,
    decoratives,
    buttonColor,
    contentAlign,
    subHeadline,
    hasPromoCodeBox,
    beforeText,
    showButtonArrow,
    afterText,
    buttons,
    cards,
    contents,
    disclaimer
  } = data

  // process background
  const [bgMp4s, bgWebms, bgImgs] = [[], [], []]

  if (backgroundMedia) {
    backgroundMedia.forEach(el => {
      if (el.file?.contentType === CONTENT_TYPE_MP4) {
        bgMp4s.push(el)
      } else if (el.file?.contentType === CONTENT_TYPE_WEBM) {
        bgWebms.push(el)
      } else {
        bgImgs.push(el)
      }
    })
  }

  if (bgMp4s.length) bgMp4s[0] = bgMp4s[Math.floor(Math.random() * bgMp4s.length)]
  if (bgWebms.length) bgWebms[0] = bgWebms[Math.floor(Math.random() * bgWebms.length)]
  if (bgImgs.length) bgImgs[0] = bgImgs[Math.floor(Math.random() * bgImgs.length)]

  const mp4Comp = bgMp4s[0] ? <source srcSet={bgMp4s[0].file.url} type={CONTENT_TYPE_MP4} /> : null
  const webmComp = bgWebms[0] ? (
    <source srcSet={bgWebms[0].file.url} type={CONTENT_TYPE_WEBM} />
  ) : null
  const bgImageComp = bgImgs[0] ? (
    <GatsbyImage image={getImage(bgImgs[0].image)} alt={bgImgs[0].altText} />
  ) : null

  let backgroundLarge = null
  let backgroundMobile = null
  if (mp4Comp || webmComp) {
    backgroundLarge = (
      <div className="bg-video">
        <div className="overlay" />
        <video autoPlay muted loop>
          {mp4Comp}
          {webmComp}
          {bgImageComp}
        </video>
      </div>
    )
    backgroundMobile = !bgImageComp ? null : <div className="bg-image">{bgImageComp}</div>
  } else {
    backgroundMobile = !bgImageComp ? null : (
      <div className="bg-image show-desktop">{bgImageComp}</div>
    )
  }

  const imageCardComp = imageCard ? (
    <div className="image-card">
      <h1>{imageCard[0].headline}</h1>
      <ul>
        {imageCard[0].description.map(item => {
          return <li key={item}>{item}</li>
        })}
      </ul>
    </div>
  ) : null

  const imageComp = !image ? null : (
    // eslint-disable-next-line jsx-a11y/img-redundant-alt
    <GatsbyImage image={getImage(image?.image)} alt={image?.altText} />
  )

  const backgroundDiamonds =
    decoratives?.indexOf('Diamonds') >= 0 ? <div className="decorative--diamonds" /> : null

  const backgroundCreditCardDecor =
    decoratives?.indexOf('Credit Card Background') >= 0 ? (
      <div className="decorative decorative--credit-card" />
    ) : null

  let headlineComp
  if (headline && headline.length) {
    if (headline.indexOf('<br') > -1 || headline.indexOf('<p') > -1) {
      /* eslint-disable react/no-danger */
      const headlineCompClasses = ['headline']
      if (headline.indexOf('<br') > -1) headlineCompClasses.push('hasbreaks')
      headlineComp = (
        <h1
          className={headlineCompClasses.join(' ')}
          dangerouslySetInnerHTML={{
            __html: headline
              .split('{mbr}')
              .join('<span class="mobile-break" />')
              .split('{dbr}')
              .join('<span class="desktop-break" />')
          }}
        />
      )
    } else {
      headlineComp = (
        <h1 className="headline">
          {headline
            .replace(/\\u00A0/g, '\u00A0')
            .split(/([[{].+?[\]}])/)
            .map(part => {
              if (part.substr(-1, 1) === ']' && part.substr(0, 1) === '[') {
                return <TypeWriter key={part} text={part.substr(1, part.length - 2).split('|')} />
              }
              if (part === '{mbr}') return <span key={part} className="mobile-break" />
              if (part === '{dbr}') return <span key={part} className="desktop-break" />
              /* eslint-disable react/no-array-index-key */
              return part
            })}
        </h1>
      )
    }
  } else {
    headlineComp = null
  }

  /* eslint-disable react/no-danger */
  let subHeadlineComp

  if (!subHeadline) {
    subHeadlineComp = null
  } else {
    let __html = subHeadline
      .split('{mbr}')
      .join('<span class="mobile-break"></span>')
      .split('{dbr}')
      .join('<span class="desktop-break"></span>')
    const divider = ' | '
    if (__html.indexOf(divider > -1)) {
      __html = `<span>${__html.replaceAll(divider, `</span><span class="vl"></span><span>`)}</span>`
    }
    subHeadlineComp = <p className="subheadline" dangerouslySetInnerHTML={{ __html }} />
  }

  const promoBoxComp =
    !hasPromoCodeBox || hasPromoCodeBox === 'None' || !slug ? null : (
      <PromoCard
        promoData={{
          landing_keyword: slug,
          type: hasPromoCodeBox
        }}
      />
    )

  const buttonArrowComp = showButtonArrow ? (
    <CurvedArrow className="button-arrow" fill="#fff" stroke="#fff" height={34} width={29} />
  ) : null

  const beforeTextComp = !beforeText ? null : (
    <div className="before-text">
      {buttonArrowComp}
      <p dangerouslySetInnerHTML={{ __html: beforeText }} />
    </div>
  )
  const afterTextComp = !afterText ? null : (
    <div className="after-text">
      <p dangerouslySetInnerHTML={{ __html: afterText }} />
    </div>
  )

  const doSlot = slotIndex => {
    const content = document.querySelector('div.hero-carousel > div.content')
    if (!content) return

    Array.from(content.classList).forEach(cl => {
      if (cl.indexOf('slot-') === 0) content.classList.remove(cl)
    })

    content.classList.add(`slot-${slotIndex}`)
  }

  /* eslint-disable react/no-array-index-key */
  const buttonComps =
    !buttons || !buttons.length ? null : (
      <div className={`buttons ${buttonColor.toLowerCase()}`}>
        {buttons.map((buttonOrForm, index) => {
          const isMainCarouselButton = index === carouselIndex
          const typeName = buttonOrForm?.__typename
          let hoverFunc = null
          if (!isMainCarouselButton && buttonOrForm.style === 'Primary') {
            hoverFunc = () => doSlot(index)
          }
          return typeName === 'ContentfulForm' ? (
            <ContentfulForm data={buttonOrForm} key={index} />
          ) : (
            <ContentfulButton
              data={buttonOrForm}
              key={index}
              isMain={isMainCarouselButton}
              onMouseOver={hoverFunc}
            />
          )
        })}
      </div>
    )

  const cardsComp =
    !cards || !cards.length ? null : (
      <div className={`blockcards ${buttonColor.toLowerCase()}`}>
        {cards.map((card, index) => (
          <BlockCard data={card} key={index} useDivOutterContainer />
        ))}
      </div>
    )

  const contentComps =
    !contents || !contents.length ? null : (
      <div className="contents">
        {contents.map(row => {
          const type = row?.sys?.contentType?.sys?.id
          if (type === 'imageSet') {
            return <FeatureV2 key={row.entryTitle} data={row} />
          }
          if (type === 'chooseUsSet') {
            return <PeopleChoose key={row.entryTitle} contentfulEntryTitle={row.entryTitle} />
          }
          if (type === 'ratesAndTermsRibbon') {
            return <RatesTermsRibbon key={row.entryTitle} data={row} />
          }
          return null
        })}
      </div>
    )

  const disclaimerComp = disclaimer?.disclaimer ? (
    <p className="disclaimer">{renderTextWithLink(disclaimer.disclaimer, 'flat-link--white')}</p>
  ) : null

  let sectionClasses = ['custom-hero']
  if (theme && theme !== 'Default (Dark)') {
    sectionClasses.push(theme.toLowerCase())
  }
  sectionClasses = sectionClasses.join(' ')

  let headlineLogosComp = []
  if (headlineLogos && headlineLogos.length) {
    headlineLogos.forEach(logo =>
      headlineLogosComp.push(
        <GatsbyImage image={getImage(logo.image)} className="logo" alt={logo.altText} />
      )
    )
    headlineLogosComp = <div className="logos">{headlineLogosComp}</div>
  }

  return (
    <section className={sectionClasses}>
      <div className={`background ${backgroundDiamonds && 'diamonds'}`}>
        {backgroundLarge}
        {backgroundMobile}
      </div>
      {backgroundDiamonds}
      <div className={`container__inner ${contentAlign?.toLowerCase() || ''}`}>
        {backgroundCreditCardDecor}
        <div className="intro">
          {imageComp}
          {headlineLogosComp}
          {headlineComp}
          {subHeadlineComp}
          {promoBoxComp}
          {buttonComps}
          {beforeTextComp}
          {afterTextComp}
          {imageCardComp}
        </div>

        {cardsComp}
        {contentComps}
        {disclaimerComp}
      </div>
    </section>
  )
}

CustomHero.defaultProps = {
  carouselIndex: -1,
  slug: null
}

CustomHero.propTypes = {
  carouselIndex: PropTypes.number,
  data: PropTypes.object.isRequired,
  slug: PropTypes.string
}

export default CustomHero
